'use client'

import React from 'react'
import {useQuery} from '@tanstack/react-query'
import {isMobile} from 'react-device-detect'
import dynamic from 'next/dynamic'
import {useTranslations} from 'next-intl'
import {css, useTheme} from '@emotion/react'

import useFullScreenFeature from '../../../../hooks/useFullScreenFeature'
import {ProcessStatus} from '../../../../constants'
import ProcessStatusInfo from '../../../../components/modules/ProcessStatusInfo'
import Paper from '../../../../components/elements/Paper'
import Processing from '../../../../components/elements/Processing'
import InputResultPanelLayout from '../../../../components/layouts/InputResultPanelLayout'


import 'leaflet/dist/leaflet.css'

const Map = dynamic(() => import('./Map'), {ssr: false})

export default function WhatIsMyIP() {
  const theme = useTheme()
  const styles = {
    top: css`
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    `,

    inputControl: css`
      display: flex;
      justify-content: space-between;
    `,

    resultControl: css`
      display: flex;
      justify-content: flex-end;
    `,

    content: css`
      padding: 16px;
      background: ${theme.palette.surface.main};
      height: 100%;

      display: flex;
      flex-direction: column;
      gap: 16px;

      & > div {
        padding: 16px;
      }
    `,

    ipSection: css`
      display: flex;
      align-items: center;
      gap: 16px;

      font-size: 1.8rem;
      font-weight: 600;
    `,

    regionSection: css`
      display: flex;
      align-items: center;
      gap: 16px;

      font-size: 1.2rem;
      font-weight: 500;
    `,

    map: css`
      width: 100%;
      height: 100%;
    `,
  }

  const t = useTranslations()

  const [processStatus, setProcessStatus] = React.useState(ProcessStatus.Idle)
  const {data, isLoading} = useQuery({
    queryKey: ['what-is-my-ip', 'my-ip'],
    queryFn: async () => {
      const resp = await fetch('/api/my-ip')
      return resp.json()
    },
  })

  React.useEffect(() => {
    if (isLoading) {
      setProcessStatus(ProcessStatus.Processing)
    } else {
      setProcessStatus(ProcessStatus.Success)
    }
  }, [isLoading])


  const [ResultFullScreenButton, ResultFullScreenContainer] = useFullScreenFeature()

  return (
    <InputResultPanelLayout
      inputLabel={t('what-is-my-ip.mode-label-info')}
      inputDescription={t('what-is-my-ip.input-description')}
      inputContent={(
        <Paper css={styles.content}>
          <Paper>
            <div css={styles.ipSection}>
              <div>{t('what-is-my-ip.label-ip')}:</div>
              {isLoading ? <Processing/> : <div>{data.data.ip}</div>}
            </div>
          </Paper>
          <Paper>
            <div css={styles.regionSection}>
              <div>{t('what-is-my-ip.label-location')}:</div>
              {isLoading ? <Processing/> :
                <div>{data.data.geo?.city}, {data.data.geo?.region} {data.data.geo?.country}</div>}
            </div>
            <div css={styles.regionSection}>
              <div css={styles.label}>{t('what-is-my-ip.label-timezone')}:</div>
              {isLoading ? <Processing/> : <div>{data.data.geo?.timezone}</div>}
            </div>
          </Paper>
        </Paper>
      )}
      inputBottom={
        <div/>
      }

      resultLabel={t('what-is-my-ip.mode-label-map')}
      resultControl={(
        !isMobile && <div css={styles.resultControl}>
          <ResultFullScreenButton/>
        </div>
      )}
      resultContent={(
        <ResultFullScreenContainer>
          {!isLoading && data.data.geo?.ll && <Map css={styles.map} location={data.data.geo.ll}/>}
        </ResultFullScreenContainer>
      )}

      resultBottom={(
        <div css={styles.bottomControlBar}>
          <ProcessStatusInfo id="process-status" processStatus={processStatus}/>
        </div>
      )}
    />
  )
}

